import { useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { getDeviceId } from '@/lib/device';

/**
 * Track each visitor (anonymous, per-device) in Supabase.
 * UUID-ul din localStorage e cheia primară — persistă la nivel de browser,
 * independent de rețea (WiFi vs date mobile).
 *
 * La fiecare load al aplicației, face upsert în `visitors`:
 *  - dacă UUID-ul e nou → insert + first_seen = now
 *  - dacă UUID-ul există → update last_seen + page_views (o data pe zi)
 *
 * IMPORTANT: face întotdeauna upsert, indiferent de localStorage,
 * ca să garanteze că rândul există în DB (ex: după reset DB).
 */
export function useVisitor() {
  useEffect(() => {
    const id = getDeviceId();
    if (!id) return;

    // Verificam daca am mai incrementat page_views azi
    const lastSeenKey = `guestlens_visitor_last_seen_${id}`;
    const isFirstVisitToday = (() => {
      const last = localStorage.getItem(lastSeenKey);
      if (!last) return true;
      const lastDate = new Date(last).toDateString();
      const today = new Date().toDateString();
      return lastDate !== today;
    })();

    const nowIso = new Date().toISOString();

    (async () => {
      try {
        if (isFirstVisitToday) {
          // Prima vizita azi: citim page_views curent sau 0 dacă nu există
          const { data: existing } = await supabase
            .from('visitors')
            .select('page_views')
            .eq('device_id', id)
            .maybeSingle();

          const prev = existing?.page_views ?? 0;

          // Upsert cu increment page_views
          const { error } = await supabase
            .from('visitors')
            .upsert(
              {
                device_id: id,
                last_seen: nowIso,
                page_views: prev + 1,
                user_agent: navigator.userAgent.slice(0, 500),
              },
              { onConflict: 'device_id' }
            );

          if (error) {
            // eslint-disable-next-line no-console
            console.warn('[useVisitor] upsert failed:', error.message);
          } else {
            // eslint-disable-next-line no-console
            console.log('[useVisitor] tracked device', id, 'page_views=', prev + 1);
            localStorage.setItem(lastSeenKey, nowIso);
          }
        } else {
          // Deja am vizitat azi — nu incrementam page_views,
          // DAR verificam ca randul exista (ex: dupa reset DB)
          const { data: existing } = await supabase
            .from('visitors')
            .select('device_id')
            .eq('device_id', id)
            .maybeSingle();

          if (!existing) {
            // Randul lipseste din DB — il cream
            const { error } = await supabase
              .from('visitors')
              .upsert(
                {
                  device_id: id,
                  last_seen: nowIso,
                  page_views: 1,
                  user_agent: navigator.userAgent.slice(0, 500),
                },
                { onConflict: 'device_id' }
              );
            if (error) {
              // eslint-disable-next-line no-console
              console.warn('[useVisitor] ensure-row failed:', error.message);
            } else {
              // eslint-disable-next-line no-console
              console.log('[useVisitor] re-created missing row for device', id);
              // Reset localStorage ca data viitoare sa incrementeze corect
              localStorage.removeItem(lastSeenKey);
            }
          }
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[useVisitor] exception:', e);
      }
    })();
  }, []);
}
